import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import API from '../../../context/API';
import { LoyaltyConfig, CustomerLoyalty, LoyaltyResponse, Merchant } from '../types';

interface LoyaltyState {
  config: LoyaltyConfig | null;
  customerLoyalties: CustomerLoyalty[];
  merchants: Merchant[];
  totalPages: number;
  totalCount: number;
  currentPage: number;
  limit: number;
  merchantId: string;
  loading: {
    config: boolean;
    customers: boolean;
    update: boolean;
    merchants: boolean;
  };
  error: string | null;
}

const initialState: LoyaltyState = {
  config: null,
  customerLoyalties: [],
  merchants: [],
  totalPages: 1,
  totalCount: 0,
  currentPage: 1,
  limit: 10,
  merchantId: '',
  loading: {
    config: false,
    customers: false,
    update: false,
    merchants: false,
  },
  error: null,
};

export const fetchLoyaltyConfig = createAsyncThunk<
  LoyaltyConfig,
  void,
  { rejectValue: string }
>(
  'loyalty/fetchConfig',
  async (_, { rejectWithValue }) => {
    try {
      const response = await API.get('profile');
      
      if (response.data.data) {
        return {
          loyaltyEnabled: response.data.data.loyaltyEnabled || false,
          loyaltyRedeemRatio: response.data.data.loyaltyRedeemRatio || '',
          loyaltySpendRatio: response.data.data.loyaltySpendRatio || '',
          minSpentForLoyalty: response.data.data.minSpentForLoyalty || '',
          minSpentForRedemption: response.data.data.minSpentForRedemption || '',
        };
      }
      return rejectWithValue('Failed to fetch loyalty configuration');
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to fetch loyalty configuration'
      );
    }
  }
);

export const updateLoyaltyConfig = createAsyncThunk<
  LoyaltyConfig,
  LoyaltyConfig,
  { rejectValue: string }
>(
  'loyalty/updateConfig',
  async (config, { rejectWithValue }) => {
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const role = user.role || '';
      const canUpdate = ['merchant', 'merchant_manager'].includes(role);
      if (canUpdate && user.id) {
        const path = `/profile/${user.id}`;
        const updatedData = {
          loyaltyEnabled: config.loyaltyEnabled,
          loyaltyRedeemRatio: config.loyaltyRedeemRatio,
          loyaltySpendRatio: config.loyaltySpendRatio,
          minSpentForLoyalty: config.minSpentForLoyalty,
          minSpentForRedemption: config.minSpentForRedemption,
        };
        await API.put(path, updatedData);
        return config;
      }
      return rejectWithValue('Only merchants or managers can update loyalty configuration');
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to update loyalty configuration'
      );
    }
  }
);

export const fetchCustomerLoyalties = createAsyncThunk<
  LoyaltyResponse,
  { merchantId: string; page: number; limit: number },
  { rejectValue: string }
>(
  'loyalty/fetchCustomers',
  async ({ merchantId, page, limit }, { rejectWithValue }) => {
    try {
      const response = await API.get('/merchant/loyalty/users', {
        params: { merchantId, page, limit },
      });
      
      if (response.data.data) {
        return {
          data: response.data.data.data || [],
          meta: {
            lastPage: response.data.data.meta?.lastPage || 1,
            total: response.data.data.meta?.total || response.data.data.meta?.totalItems || 0,
            page: response.data.data.meta?.page || page,
            limit: response.data.data.meta?.limit || limit,
          },
        };
      }
      return rejectWithValue('Failed to fetch customer loyalties');
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to fetch customer loyalties'
      );
    }
  }
);

export const fetchMerchants = createAsyncThunk<
  Merchant[],
  void,
  { rejectValue: string }
>(
  'loyalty/fetchMerchants',
  async (_, { rejectWithValue }) => {
    try {
      const response = await API.get('/merchant');
      
      if (response.data.data) {
        return response.data.data || [];
      }
      return rejectWithValue('Failed to fetch merchants');
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to fetch merchants'
      );
    }
  }
);

const loyaltySlice = createSlice({
  name: 'loyalty',
  initialState,
  reducers: {
    setMerchantId: (state, action) => {
      state.merchantId = action.payload;
      state.currentPage = 1;
    },
    setCurrentPage: (state, action) => {
      state.currentPage = action.payload;
    },
    setLimit: (state, action) => {
      state.limit = action.payload;
      state.currentPage = 1;
    },
    toggleCustomerCollapse: (state, action) => {
      const id = action.payload;
      state.customerLoyalties = state.customerLoyalties.map((loyalty) =>
        loyalty.id === id
          ? { ...loyalty, isOpen: !loyalty.isOpen }
          : { ...loyalty, isOpen: false }
      );
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch config
      .addCase(fetchLoyaltyConfig.pending, (state) => {
        state.loading.config = true;
        state.error = null;
      })
      .addCase(fetchLoyaltyConfig.fulfilled, (state, action) => {
        state.loading.config = false;
        state.config = action.payload;
      })
      .addCase(fetchLoyaltyConfig.rejected, (state, action) => {
        state.loading.config = false;
        state.error = action.payload as string;
      })
      // Update config
      .addCase(updateLoyaltyConfig.pending, (state) => {
        state.loading.update = true;
        state.error = null;
      })
      .addCase(updateLoyaltyConfig.fulfilled, (state, action) => {
        state.loading.update = false;
        state.config = action.payload;
      })
      .addCase(updateLoyaltyConfig.rejected, (state, action) => {
        state.loading.update = false;
        state.error = action.payload as string;
      })
      // Fetch customers
      .addCase(fetchCustomerLoyalties.pending, (state) => {
        state.loading.customers = true;
        state.error = null;
      })
      .addCase(fetchCustomerLoyalties.fulfilled, (state, action) => {
        state.loading.customers = false;
        state.customerLoyalties = action.payload.data.map((loyalty) => ({
          ...loyalty,
          isOpen: false,
        }));
        state.totalPages = action.payload.meta.lastPage;
        state.totalCount = action.payload.meta.total || 0;
      })
      .addCase(fetchCustomerLoyalties.rejected, (state, action) => {
        state.loading.customers = false;
        state.error = action.payload as string;
      })
      // Fetch merchants
      .addCase(fetchMerchants.pending, (state) => {
        state.loading.merchants = true;
        state.error = null;
      })
      .addCase(fetchMerchants.fulfilled, (state, action) => {
        state.loading.merchants = false;
        state.merchants = action.payload;
      })
      .addCase(fetchMerchants.rejected, (state, action) => {
        state.loading.merchants = false;
        state.error = action.payload as string;
      });
  },
});

export const {
  setMerchantId,
  setCurrentPage,
  setLimit,
  toggleCustomerCollapse,
  clearError,
} = loyaltySlice.actions;

export default loyaltySlice.reducer;
